/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * History Page - Displays all past design submissions
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Trash2, ShoppingBag, Clock, ChevronLeft, Image as ImageIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import logo from '../data/banner/logo-xoa-background.png';
import Footer from '../components/Footer';
import { indexedDBService } from '../services/indexedDbService';
import { addToHaravanCart } from '../services/haravanCartService';

interface PreviousDesign {
    id: string;
    productType: string;
    createdAt: Date;
    orderId: string;
    variantId?: number; // Haravan variant ID for re-purchasing
    productName?: string; // Product name for display
}

const HistoryPage: React.FC = () => {
    const navigate = useNavigate();
    const [designs, setDesigns] = useState<PreviousDesign[]>([]);
    const [thumbnails, setThumbnails] = useState<Record<string, string>>({});
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const loadDesigns = async () => {
            try {
                const visitorId = localStorage.getItem('veritas_visitor_id');
                if (!visitorId) {
                    setIsLoading(false);
                    return;
                }

                const savedDesigns = localStorage.getItem(`designs_${visitorId}`);
                if (savedDesigns) {
                    const parsedDesigns = JSON.parse(savedDesigns).map((d: any) => ({
                        ...d,
                        createdAt: new Date(d.createdAt)
                    }));

                    // Sort by newest first
                    const sorted = parsedDesigns.sort((a: PreviousDesign, b: PreviousDesign) =>
                        b.createdAt.getTime() - a.createdAt.getTime()
                    );

                    setDesigns(sorted);

                    // Load thumbnails from IndexedDB
                    const thumbMap: Record<string, string> = {};
                    for (const design of sorted) {
                        const img = await indexedDBService.getThumbnail(design.orderId);
                        if (img) thumbMap[design.orderId] = img;
                    }
                    setThumbnails(thumbMap);
                }
            } catch (err) {
                console.error('Failed to load history:', err);
            } finally {
                setIsLoading(false);
            }
        };

        loadDesigns();
    }, []);

    const handleDelete = async (orderId: string) => {
        if (!window.confirm('Bạn có chắc muốn xóa thiết kế này khỏi lịch sử?')) return;

        try {
            const visitorId = localStorage.getItem('veritas_visitor_id');
            if (!visitorId) return;

            // Update localStorage
            const updatedDesigns = designs.filter(d => d.orderId !== orderId);
            localStorage.setItem(`designs_${visitorId}`, JSON.stringify(updatedDesigns));
            setDesigns(updatedDesigns);

            // Update IndexedDB
            await indexedDBService.deleteThumbnail(orderId);

            // Update thumbnails state
            const newThumbs = { ...thumbnails };
            delete newThumbs[orderId];
            setThumbnails(newThumbs);
        } catch (err) {
            console.error('Failed to delete design:', err);
        }
    };

    const getProductTypeLabel = (productType: string) => {
        const labels: Record<string, string> = {
            'phone_case': 'Ốp lưng điện thoại',
            'coffee_cup': 'Cốc bã cà phê'
        };
        return labels[productType] || productType;
    };

    const formatDate = (date: Date) => {
        return new Intl.DateTimeFormat('vi-VN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }).format(date);
    };

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col overflow-auto">
            {/* Redesigned Header */}
            <header className="bg-white border-b border-gray-200 sticky top-0 z-40 flex-shrink-0">
                <div className="max-w-7xl mx-auto px-2 sm:px-4 py-1 flex items-center justify-between min-h-[64px]">
                    {/* Left: Back & Logo */}
                    <div className="flex items-center gap-1 sm:gap-4 flex-shrink-0">
                        <button
                            onClick={() => navigate(-1)}
                            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                            title="Quay lại"
                        >
                            <ChevronLeft className="w-6 h-6 text-gray-700" />
                        </button>
                        <div className="flex items-center h-12 sm:h-16 overflow-visible py-1">
                            <img 
                                src={logo} 
                                alt="AirX" 
                                className="h-16 sm:h-24 w-auto object-contain transform -translate-y-1 drop-shadow-sm" 
                            />
                        </div>
                    </div>

                    {/* Center: Title */}
                    <h1 className="text-xs sm:text-base lg:text-lg font-extrabold text-gray-900 mx-1 text-center flex-1 line-clamp-1 uppercase tracking-tight">
                        Lịch sử thiết kế
                    </h1>

                    {/* Right: My Design Button */}
                    <div className="flex-shrink-0">
                        <a
                            href="/history"
                            className="flex items-center gap-1.5 px-2.5 py-2 bg-black text-white hover:bg-gray-800 rounded-xl transition-all text-[10px] sm:text-xs font-bold shadow-md active:scale-95"
                        >
                            <Clock className="w-3.5 h-3.5" />
                            <span>My design</span>
                        </a>
                    </div>
                </div>
            </header>

            <main className="flex-1 max-w-7xl mx-auto w-full px-4 py-8 md:py-12">
                {isLoading ? (
                    <div className="flex flex-col items-center justify-center py-20">
                        <div className="w-10 h-10 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin mb-4" />
                        <p className="text-slate-500 font-medium">Đang tải lịch sử...</p>
                    </div>
                ) : designs.length === 0 ? (
                    <div className="bg-white rounded-3xl p-12 text-center shadow-sm border border-slate-100 max-w-2xl mx-auto">
                        <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6">
                            <Clock className="w-10 h-10 text-slate-300" />
                        </div>
                        <h2 className="text-2xl font-bold text-slate-800 mb-2">Chưa có thiết kế nào</h2>
                        <p className="text-slate-500 mb-8 max-w-md mx-auto">
                            Bạn chưa thực hiện thiết kế sản phẩm nào. Hãy bắt đầu sáng tạo ngay bây giờ!
                        </p>
                        <div className="flex justify-center">
                            <button
                                onClick={() => navigate('/')}
                                className="bg-black text-white px-10 py-4 rounded-2xl font-bold hover:bg-gray-800 transition-all flex items-center justify-center gap-2 shadow-lg active:scale-95"
                            >
                                <ShoppingBag className="w-5 h-5" />
                                Thiết kế ngay
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {designs.map((design, index) => (
                            <motion.div
                                key={design.orderId}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.05 }}
                                className="bg-white rounded-2xl shadow-sm hover:shadow-md transition-all border border-slate-100 overflow-hidden group flex flex-col"
                            >
                                {/* Image Section */}
                                <div className="relative aspect-square bg-slate-50 overflow-hidden flex items-center justify-center">
                                    {thumbnails[design.orderId] ? (
                                        <img
                                            src={thumbnails[design.orderId]}
                                            alt={design.orderId}
                                            className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                                        />
                                    ) : (
                                        <div className="text-slate-300 flex flex-col items-center gap-2">
                                            <span className="text-6xl">
                                                {design.productType === 'phone_case' ? '📱' : '☕'}
                                            </span>
                                        </div>
                                    )}

                                    {/* Delete Button Overlay */}
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDelete(design.orderId);
                                        }}
                                        className="absolute top-2 right-2 p-2 bg-white shadow-md text-red-500 rounded-full z-20 hover:bg-red-50 active:scale-95 transition-all border border-red-100"
                                        title="Xóa khỏi lịch sử"
                                    >
                                        <Trash2 className="w-5 h-5" />
                                    </button>

                                    {/* Order ID Badge */}
                                    <div className="absolute bottom-3 left-3 px-3 py-1 bg-black/50 backdrop-blur-md text-white text-[10px] font-mono rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                                        {design.orderId}
                                    </div>
                                </div>

                                {/* Content Section */}
                                <div className="p-5 flex-1 flex flex-col">
                                    <div className="mb-4">
                                        <h3 className="font-bold text-slate-900 group-hover:text-indigo-600 transition-colors">
                                            {getProductTypeLabel(design.productType)}
                                        </h3>
                                        <div className="text-xs text-slate-500 flex items-center gap-1 mt-1">
                                            <Clock className="w-3 h-3" />
                                            {formatDate(design.createdAt)}
                                        </div>
                                    </div>

                                    <div className="mt-auto space-y-2">
                                        <button
                                            onClick={() => {
                                                navigator.clipboard.writeText(design.orderId);
                                                alert('Đã copy mã đơn hàng!');
                                            }}
                                            className="w-full bg-slate-100 text-slate-700 py-2.5 rounded-xl text-sm font-bold hover:bg-slate-200 transition-colors flex items-center justify-center gap-2"
                                        >
                                            Copy Mã Đơn Hàng
                                        </button>
                                        {design.variantId ? (
                                            <button
                                                onClick={() => {
                                                    // Form POST will redirect to cart automatically
                                                    addToHaravanCart({
                                                        variantId: design.variantId!,
                                                        designId: design.orderId,
                                                        quantity: 1
                                                    });
                                                }}
                                                className="w-full bg-indigo-600 text-white py-2.5 rounded-xl text-sm font-bold hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2 shadow-sm"
                                            >
                                                <ShoppingBag className="w-4 h-4" />
                                                Mua Lại Sản Phẩm
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => {
                                                    navigator.clipboard.writeText(design.orderId);
                                                    alert('Đã copy mã đơn hàng! Vui lòng dán mã vào giỏ hàng khi checkout.');
                                                    window.open('https://custom.airxcoffee.com/cart', '_blank');
                                                }}
                                                className="w-full bg-slate-600 text-white py-2.5 rounded-xl text-sm font-bold hover:bg-slate-700 transition-colors flex items-center justify-center gap-2 shadow-sm"
                                            >
                                                <ShoppingBag className="w-4 h-4" />
                                                Xem Giỏ Hàng
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                )}
            </main>

            <Footer />
        </div>
    );
};

export default HistoryPage;
